import { getCapHeight } from "@capsizecss/core";
import clsx from "clsx";
import { jsx, jsxs } from "react/jsx-runtime";
import { createContext, useRef, useState, useEffect } from "react";
import { ProgressBarChart } from "./styles/components/ProgressBarChart/ProgressBarChart.mjs";
import { StackBarChart } from "./styles/components/StackBarChart/StackBarChart.mjs";
import { Box, Inline, Text, Stack, Columns, Column } from "braid-design-system";
const colour = {
  brand: {
    50: "#EDF4FF",
    100: "#D3DFF4",
    200: "#BACBE8",
    300: "#8CA6D3",
    400: "#6385BF",
    500: "#4168AA",
    600: "#244E95",
    700: "#0D3880",
    800: "#042D73",
    900: "#00225A"
  },
  accent: {
    50: "#FFEDF7",
    100: "#FFCAE7",
    200: "#FFA7D6",
    300: "#F86CB5",
    400: "#EF3596",
    500: "#E60278",
    600: "#BD0B67",
    700: "#A60057",
    800: "#860047",
    900: "#660036"
  },
  grey: {
    50: "#F8F9FC",
    100: "#F1F3F9",
    200: "#E8EBF3",
    300: "#CDD6E9",
    400: "#A7B2C9",
    500: "#69768C",
    600: "#51596C",
    700: "#333A49",
    800: "#1E222A",
    900: "#12151A"
  },
  yellow: {
    50: "#FFF2DB",
    100: "#FFEBB7",
    200: "#FFE892",
    300: "#FFD851",
    400: "#FFC600",
    500: "#E09C00",
    600: "#C27800",
    700: "#A35A00",
    800: "#854200",
    900: "#662E00"
  },
  orange: {
    50: "#FFE4DB",
    100: "#FFDACC",
    200: "#FFD1BD",
    300: "#FFB390",
    400: "#FC8E53",
    500: "#F76B0C",
    600: "#D35800",
    700: "#AF4A00",
    800: "#8A3900",
    900: "#662900"
  },
  red: {
    50: "#FFDBE0",
    100: "#FED2D8",
    200: "#FCC8CF",
    300: "#F1A3AD",
    400: "#E6707F",
    500: "#DB364B",
    600: "#D0011B",
    700: "#AD0016",
    800: "#890012",
    900: "#66000E"
  },
  purple: {
    50: "#F9EDFF",
    100: "#E7D4F1",
    200: "#D6BCE3",
    300: "#B88FCC",
    400: "#9B68B5",
    500: "#81469D",
    600: "#682B86",
    700: "#58107B",
    800: "#4D0571",
    900: "#440066"
  },
  violet: {
    50: "#EFEDFF",
    100: "#D9D5FB",
    200: "#C4BDF8",
    300: "#9B8EEA",
    400: "#7A63DD",
    500: "#5F3CD0",
    600: "#431BB6",
    700: "#2A029B",
    800: "#220081",
    900: "#1A0066"
  },
  indigo: {
    50: "#EDF4FF",
    100: "#D0E0FB",
    200: "#B3CCF7",
    300: "#7FA7EA",
    400: "#3F85E9",
    500: "#2765CF",
    600: "#0E4BB5",
    700: "#00399B",
    800: "#002F80",
    900: "#002566"
  },
  blue: {
    50: "#EDF9FF",
    100: "#CFEEFE",
    200: "#B1E4FD",
    300: "#7CCBF3",
    400: "#4AB4E8",
    500: "#1D9EDE",
    600: "#0682C0",
    700: "#006CA2",
    800: "#005884",
    900: "#004466"
  },
  cyan: {
    50: "#EDFEFF",
    100: "#CCF6F8",
    200: "#ADEEF0",
    300: "#79DBE0",
    400: "#4AC9CF",
    500: "#22B7BE",
    600: "#00A5AD",
    700: "#008F95",
    800: "#00797E",
    900: "#006366"
  },
  green: {
    50: "#F0FFED",
    100: "#D8F5D0",
    200: "#C2EBB5",
    300: "#9AD885",
    400: "#74C45B",
    500: "#50B137",
    600: "#309D18",
    700: "#178A00",
    800: "#007801",
    900: "#006611"
  }
};
const systemColour = {
  black: "#000000",
  white: "#FFFFFF"
};
const layer = {
  layer1: 0,
  layer2: 10,
  layer3: 100
};
const motion = {
  veryFast: "30ms",
  fast: "150ms",
  normal: "300ms",
  slow: "600ms",
  verySlow: "900ms"
};
const motionMilliseconds = {
  veryFast: 30,
  fast: 150,
  normal: 300,
  slow: 600,
  verySlow: 900
};
const fontMetrics = {
  capHeight: 1456,
  ascent: 1900,
  descent: -500,
  lineGap: 0,
  unitsPerEm: 2048
};
const typography = {
  caption1: {
    capHeight: getCapHeight({
      fontSize: 12,
      fontMetrics
    }),
    pointSize: 12
  },
  caption2: {
    capHeight: getCapHeight({
      fontSize: 10,
      fontMetrics
    }),
    pointSize: 10
  }
};
const tokens = {
  colour,
  layer,
  motion,
  motionMilliseconds,
  systemColour,
  typography
};
const chartTheme = {
  tokens
};
const months = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec"
];
const MINUTE = 6e4;
const MINUTES = MINUTE;
const HOUR = 60 * MINUTES;
const HOURS = HOUR;
const DAY = 24 * HOURS;
const DAYS = DAY;
function minutes(amount) {
  return amount * MINUTES;
}
function hours(amount) {
  return amount * HOURS;
}
function days(amount) {
  return amount * DAYS;
}
function isValidDate(date) {
  return !isNaN(date.getTime());
}
function addDays(d, n) {
  return new Date(new Date(d).setUTCDate(d.getUTCDate() + n));
}
function addMonths(d, m) {
  return new Date(new Date(d).setUTCMonth(d.getUTCMonth() + m));
}
function addYears(d, n) {
  return new Date(new Date(d).setUTCFullYear(d.getUTCFullYear() + n));
}
function setDayOfMonth(d, dayOfMonth) {
  return new Date(new Date(d).setUTCDate(dayOfMonth));
}
function daysFromDates(d1, d2) {
  const deltaSeconds = d2.getTime() - d1.getTime();
  return Math.floor(deltaSeconds / (1e3 * 3600 * 24));
}
function daysFromDateStrings(d1, d2) {
  return daysFromDates(new Date(d1), new Date(d2));
}
function diffFromNow(date) {
  const now = Date.now();
  return now.valueOf() - date.valueOf();
}
function daysAgoFromString(date) {
  const d = new Date(date);
  const today = /* @__PURE__ */ new Date();
  const deltaSeconds = today.getTime() - d.getTime();
  return Math.floor(deltaSeconds / (1e3 * 3600 * 24));
}
function isoDateFromDate(d) {
  return d.toISOString().split("T")[0];
}
function isoDateFromString(d) {
  if (d === void 0 || d.length === 0) {
    return "";
  }
  return isoDateFromDate(new Date(d));
}
function addedAetOffsetStringFromDate(d) {
  const timezoneOffset = 396e5;
  const target = new Date(d.getTime() + timezoneOffset);
  const monthStr = (target.getUTCMonth() + 1).toString().padStart(2, "0");
  const dayStr = target.getUTCDate().toString().padStart(2, "0");
  return `${target.getUTCFullYear()}-${monthStr}-${dayStr}`;
}
function prettyDateFromString(date) {
  const d = new Date(date);
  if (!isValidDate(d)) {
    return "";
  }
  return `${d.getUTCDate()} ${months[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}
function prettyMonthFromString(date) {
  const d = new Date(date);
  if (!isValidDate(d)) {
    return "";
  }
  return `${months[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}
const formatDayOfTheMonth = (day) => {
  if (day < 4 || day > 20) {
    if (day % 10 === 1) {
      return `${day}st`;
    } else if (day % 10 === 2) {
      return `${day}nd`;
    } else if (day % 10 === 3) {
      return `${day}rd`;
    }
  }
  return `${day}th`;
};
const weekDays = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday"
];
const fullMonthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December"
];
function prettyDateTimeFromString(date) {
  const ampm = date.getHours() >= 12 ? "pm" : "am";
  const hourString = date.getHours() % 12 || 12;
  const minuteString = (date.getMinutes() < 10 ? "0" : "") + date.getMinutes();
  return `${weekDays[date.getDay()]} the ${formatDayOfTheMonth(
    date.getDate()
  )} of ${fullMonthNames[date.getMonth()]} at ${hourString}:${minuteString} ${ampm}`;
}
function shortListingDateFromString(date) {
  const diff = diffFromNow(new Date(date));
  let number;
  if (diff < minutes(5.5)) {
    number = 5;
    return `${number}m ago`;
  }
  if (diff < minutes(59.5)) {
    number = Math.round(diff / MINUTE);
    return `${number}m ago`;
  }
  if (diff < hours(23.5)) {
    number = Math.round(diff / HOUR);
    return `${number}h ago`;
  }
  if (diff < days(30.5)) {
    number = Math.round(diff / DAY);
    return `${number}d ago`;
  }
  number = 30;
  return `${number}d+ ago`;
}
function isRecentFromString(date) {
  return diffFromNow(new Date(date)) < hours(23.5);
}
const relativeDateFromDate = (baseDate) => (date) => {
  const deltaSeconds = baseDate.getTime() - date.getTime();
  const deltaHours = Math.floor(deltaSeconds / (1e3 * 3600));
  if (deltaHours <= 21) {
    return `${deltaHours} hours ago`;
  }
  if (deltaHours <= 35) {
    return "1 day ago";
  }
  const deltaDays = Math.round(deltaSeconds / (1e3 * 3600 * 24));
  if (deltaDays <= 25) {
    return `${deltaDays} days ago`;
  }
  if (deltaDays <= 45) {
    return "1 month ago";
  }
  const deltaMonths = Math.round(deltaSeconds / (1e3 * 3600 * 24 * 30));
  return `${deltaMonths} months ago`;
};
const dateFormatter = {
  addDays,
  addMonths,
  addYears,
  daysAgoFromString,
  daysFromDateStrings,
  fullMonthNames,
  isoDateFromDate,
  isoDateFromString,
  isRecentFromString,
  prettyDateFromString,
  prettyDateTimeFromString,
  prettyMonthFromString,
  relativeDateFromDate,
  setDayOfMonth,
  shortListingDateFromString,
  addedAetOffsetStringFromDate
};
const gridUnit = 4;
function gUnit(multiplier) {
  return multiplier * gridUnit;
}
const grid = {
  gUnit
};
function createLayer({
  className,
  parent
}) {
  const layer2 = parent.append("g");
  if (className) {
    layer2.attr("class", clsx(className));
  }
  return layer2;
}
const layerFactory = {
  createLayer
};
function thousandSeparatedNumberFromNumber({
  fixedTwoDigitDecimal = true,
  value = 0
}) {
  const hasDecimal = value.toString().includes(".");
  const formattedValue = hasDecimal && fixedTwoDigitDecimal ? value.toFixed(2) : value.toString();
  const digits = formattedValue.split("");
  const decimalIndex = digits.indexOf(".");
  const lastWholeNumber = decimalIndex === -1 ? digits.length : decimalIndex;
  return digits.reduce((acc, d, i) => {
    const wholeNumberPosition = lastWholeNumber - 1 - i;
    const isWholeNumber = wholeNumberPosition > 0;
    const thousandSeparator = isWholeNumber && wholeNumberPosition % 3 === 0 ? "," : "";
    return `${acc}${d}${thousandSeparator}`;
  }, "");
}
function unitNumberFromNumber(value = 0) {
  if (value >= 1e9) {
    return {
      suffix: "B",
      value: Math.round(value / 1e8) / 10
    };
  } else if (value >= 1e6) {
    return {
      suffix: "M",
      value: Math.round(value / 1e5) / 10
    };
  } else if (value >= 1e3) {
    return {
      suffix: "K",
      value: Math.round(value / 100) / 10
    };
  }
  return {
    value
  };
}
const numberFormatter = {
  thousandSeparatedNumberFromNumber,
  unitNumberFromNumber
};
function percentFromNumbers({
  numerator,
  denominator
}) {
  if (denominator === 0) {
    return "invalid";
  }
  return 100 * numerator / denominator;
}
function percentWithEdgeDecimalsNumbers({
  numerator,
  denominator
}) {
  if (denominator === 0) {
    return "0";
  }
  const percent = 100 * numerator / denominator;
  return percentWithEdgeDecimalsFromPercent({ percent });
}
function percentWithEdgeDecimalsFromPercent({
  percent
}) {
  const positivityMultiplier = percent > 0 ? 1 : -1;
  const p = Math.abs(percent);
  const showPercent = p > 99 && p < 100 || p > 0 && p < 1;
  if (!showPercent) {
    return `${Math.round(positivityMultiplier * p)}`;
  }
  const clampPercent = p > 99.99 || p < 0.01;
  if (clampPercent) {
    return p > 99.99 ? `${99.99 * positivityMultiplier}` : `${0.01 * positivityMultiplier}`;
  }
  const decimalResult = (p * positivityMultiplier).toFixed(2);
  const hasPointlessZeros = decimalResult.includes(".00");
  if (hasPointlessZeros) {
    return `${Math.round(p * positivityMultiplier)}`;
  }
  return decimalResult;
}
function relativePercentFromNumbers({
  numerator,
  denominator
}) {
  if (numerator === 0 || denominator === 0 || denominator === void 0) {
    return "invalid";
  }
  return 100 * numerator / denominator - 100;
}
function comparisonFromMetrics({
  comparisonMetric,
  metric
}) {
  const relativePercent = relativePercentFromNumbers({
    numerator: metric,
    denominator: comparisonMetric
  });
  const isPositiveComparison = comparisonMetric !== void 0 && metric >= comparisonMetric;
  if (relativePercent === void 0 || relativePercent === "invalid") {
    return "invalid";
  }
  if (relativePercent >= 100) {
    const multiplier = Math.round(relativePercent / 10) / 10 + 1 * Number(isPositiveComparison ? 1 : -1);
    return `${Math.abs(multiplier)}${isPositiveComparison ? "x more" : "x less"}`;
  }
  const prettyPercent = percentWithEdgeDecimalsFromPercent({
    percent: Math.abs(relativePercent)
  });
  return `${prettyPercent}${isPositiveComparison ? "% more" : "% less"}`;
}
const percentFormatter = {
  comparisonFromMetrics,
  percentFromNumbers,
  percentWithEdgeDecimalsNumbers,
  percentWithEdgeDecimalsFromPercent,
  relativePercentFromNumbers
};
function isNullFromString(string) {
  return string === null || string === void 0 || string.length === 0;
}
const stringFormatter = {
  isNullFromString
};
const index = {
  grid,
  dateFormatter,
  layerFactory,
  numberFormatter,
  percentFormatter,
  stringFormatter
};
const ChartThemeContext = createContext(null);
const ChartThemeProvider = ({ children }) => /* @__PURE__ */ jsx(ChartThemeContext.Provider, { value: chartTheme, children });
const StackBarChartLegend = ({
  legendProps: { payload },
  isNVLEnabled
}) => /* @__PURE__ */ jsx(
  Box,
  {
    paddingTop: isNVLEnabled ? "none" : "large",
    paddingBottom: isNVLEnabled ? "medium" : "none",
    children: /* @__PURE__ */ jsx(Inline, { space: "gutter", children: payload == null ? void 0 : payload.map((p, index2) => /* @__PURE__ */ jsxs(Box, { display: "flex", flexWrap: "nowrap", children: [
      /* @__PURE__ */ jsx(
        Box,
        {
          style: {
            width: isNVLEnabled ? "10px" : "12px",
            // The width is set to 10px so that it can form a circle instead of oval when borderRadius is set to full
            height: "10px",
            backgroundColor: p.color
          },
          marginRight: "xsmall",
          borderRadius: isNVLEnabled ? "full" : "none"
        }
      ),
      /* @__PURE__ */ jsx(Text, { size: isNVLEnabled ? "small" : "standard", children: p.value })
    ] }, `item-${index2}`)) })
  }
);
const StackBarChartTooltip = ({
  tooltipProps: { active, payload, label },
  noDataTooltip,
  locale,
  isNVLEnabled
}) => {
  var _a, _b;
  const numberFormatter2 = new Intl.NumberFormat(locale);
  const maximumRef = useRef(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    setWidth(maximumRef.current ? maximumRef.current.offsetWidth : 0);
  }, [(_a = maximumRef == null ? void 0 : maximumRef.current) == null ? void 0 : _a.offsetWidth]);
  const tooltipTotal = (payload == null ? void 0 : payload.reduce(
    (total, currentValue) => total + (currentValue.value ?? 0),
    0
  )) ?? 0;
  if (!active) return null;
  const hasAdTypeCount = (_b = payload == null ? void 0 : payload.reduce(
    (maxValue, currentValue) => (maxValue.value ?? 0) > (currentValue.value ?? 0) ? maxValue : currentValue,
    { value: 0 }
  )) == null ? void 0 : _b.value;
  if (!hasAdTypeCount) {
    return /* @__PURE__ */ jsx(
      Box,
      {
        boxShadow: isNVLEnabled ? "borderNeutralLight" : void 0,
        background: "body",
        borderRadius: isNVLEnabled ? "standard" : void 0,
        padding: "small",
        children: /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
          /* @__PURE__ */ jsx(Text, { weight: "strong", size: "small", children: label }),
          /* @__PURE__ */ jsx(Text, { size: "small", children: noDataTooltip })
        ] })
      }
    );
  }
  return /* @__PURE__ */ jsx(
    Box,
    {
      boxShadow: isNVLEnabled ? "borderNeutralLight" : void 0,
      borderRadius: isNVLEnabled ? "standard" : void 0,
      background: "body",
      padding: "small",
      maxWidth: "xsmall",
      children: /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
        /* @__PURE__ */ jsxs(Columns, { space: "small", alignY: "center", children: [
          /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsx(Text, { weight: "strong", size: "small", maxLines: 1, children: label }) }),
          /* @__PURE__ */ jsx(Column, { width: "content", children: /* @__PURE__ */ jsx(Box, { textAlign: "right", children: /* @__PURE__ */ jsx(Box, { display: "inlineBlock", ref: maximumRef, children: /* @__PURE__ */ jsx(Text, { weight: "strong", size: "small", align: "right", children: numberFormatter2.format(tooltipTotal) }) }) }) })
        ] }),
        /* @__PURE__ */ jsx(Stack, { space: "xsmall", children: payload.map((p) => /* @__PURE__ */ jsxs(Columns, { space: "small", alignY: "center", children: [
          /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsxs(Columns, { space: "xsmall", children: [
            /* @__PURE__ */ jsx(Column, { width: "content", children: /* @__PURE__ */ jsx(
              Box,
              {
                style: {
                  width: isNVLEnabled ? "10px" : "12px",
                  // The width is set to 10px so that it can form a circle instead of oval when borderRadius is set to full
                  height: "10px",
                  backgroundColor: p.color
                },
                borderRadius: isNVLEnabled ? "full" : "none"
              }
            ) }),
            /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsx(Text, { size: "xsmall", maxLines: 1, children: p.name }) })
          ] }) }),
          /* @__PURE__ */ jsx(Column, { width: "content", children: /* @__PURE__ */ jsx(Box, { style: { width: `${width}px` }, children: /* @__PURE__ */ jsx(Text, { size: "xsmall", align: "right", children: numberFormatter2.format(p.value) }) }) })
        ] }, p.name)) })
      ] })
    }
  );
};
export {
  ChartThemeProvider,
  ProgressBarChart,
  StackBarChart,
  StackBarChartLegend,
  StackBarChartTooltip,
  chartTheme,
  index as chartUtils
};
